<template>
  <div class="award-values-wrapper">
    <div class="pill-row">
      <button
        v-for="(minor, idx) in currentMinors"
        :key="`pill-${idx}-${minor}`"
        type="button"
        class="pill"
        :class="{ active: idx === defaultIndex }"
        disabled
      >
        {{ formatMinorToDisplay(minor) }}
      </button>
      <button type="button" class="pill edit-pill" @click="openModal">
        <font-awesome-icon :icon="['fal', 'pen-to-square']" />
        Edit
      </button>
    </div>

    <b-modal
      ref="modalRef"
      title="Manage reward amounts"
      centered
      @show="onModalShow"
      @hidden="onModalHidden"
      body-class="p-0"
      dialog-class="modal-dialog"
    >
      <div class="px-5 py-4 pb-5 modal-body-content">
        <div class="intro">
          <div class="sub">
            Select the monetary amounts that can be attached to open recognitions. If an employee chooses to suggest a
            monetary reward, the default value will be pre-selected.
          </div>
        </div>

        <div class="table-head">
          <div class="col-amount">Amount</div>
          <div class="col-default">Default</div>
          <div class="col-action">Action</div>
        </div>

        <div class="editor-list">
          <div v-for="(entry, idx) in entries" :key="`entry-${idx}`" class="editor-row">
            <div class="col-amount">
              <app-input
                v-model="entries[idx]"
                :validate-immediate="false"
                :name="`Reward amount option ${idx + 1}`"
                type="number"
                step="0.01"
                placeholder="0.00"
                inputmode="decimal"
                :prepend="currencySymbol"
                class="amount-input"
                @input="onAmountInput(idx, $event)"
                @keydown="onAmountKeydown"
                @blur="onAmountBlur(idx)"
                @change="onAmountBlur(idx)"
              />
            </div>

            <div class="col-default">
              <b-form-radio
                v-model="defaultIndex"
                :value="idx"
                name="default-award-value"
                class="default-radio"
                :disabled="entries.length <= 1"
              />
            </div>

            <div class="col-action">
              <b-button
                variant="outline-danger"
                size="xs"
                class="remove-btn"
                :disabled="entries.length <= 1"
                @click="removeEntry(idx)"
              >
                <font-awesome-icon :icon="['fad', 'trash']" />
              </b-button>
            </div>
          </div>
        </div>

        <div class="footer-actions">
          <b-button variant="outline-primary" @click="addEntry" size="sm">
            <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />
            Add amount
          </b-button>
        </div>

        <small v-if="validationError" class="text-danger">{{ validationError }}</small>
      </div>

      <template #modal-footer>
        <b-button variant="outline-primary" @click="closeModal">Cancel</b-button>
        <b-button variant="primary" @click="saveAndClose">Save & Close</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import AppInput from '@/components/AppInput.vue';

const props = defineProps({
  value: {
    type: [Number, Array, null],
    default: undefined,
  },
  defaultMinor: {
    type: Number,
    default: undefined,
  },
  minAmount: {
    type: Number,
    default: 0.01,
  },
  currencySymbol: {
    type: String,
    default: '£',
  },
});
const emit = defineEmits(['input', 'update:defaultMinor']);

const modalRef = ref(null);
const validationError = ref('');

const currentMinors = computed(() => {
  const v = props.value;
  if (Array.isArray(v)) {
    return v.filter(n => Number.isFinite(n));
  }
  if (Number.isFinite(v)) {
    return [v];
  }
  return [];
});

const entries = ref([]);
const defaultIndex = ref(0);

const openModal = () => {
  if (modalRef.value) {
    modalRef.value.show();
  }
};

const closeModal = () => {
  if (modalRef.value) {
    modalRef.value.hide();
  }
};

const onModalShow = () => {
  const v = props.value;
  let list = [];
  if (Array.isArray(v)) {
    list = v.filter(n => Number.isFinite(n));
  } else if (Number.isFinite(v)) {
    list = [v];
  }
  if (!list.length) {
    entries.value = [''];
    defaultIndex.value = 0;
  } else {
    entries.value = list.map(minor => toTwoDecimalsOrEmpty(minor / 100));
    // Try to align the default index with the provided default minor (if any)
    if (Number.isFinite(props.defaultMinor)) {
      const idx = list.findIndex(minor => minor === props.defaultMinor);
      defaultIndex.value = idx >= 0 ? idx : 0;
    } else {
      defaultIndex.value = 0;
    }
  }
  validationError.value = '';
};

const onModalHidden = () => {
  validationError.value = '';
};

const addEntry = () => {
  entries.value.push('');
  if (entries.value.length === 1) {
    defaultIndex.value = 0;
  }
};

const removeEntry = idx => {
  entries.value.splice(idx, 1);

  // Keep the default attached to the same value where possible:
  // - If we removed the default row, move the default to the next row (or previous if we removed the last).
  // - If we removed a row before the default, shift the default index left by one.
  // - If we removed a row after the default, the default index is unchanged.
  if (!entries.value.length) {
    defaultIndex.value = 0;
    return;
  }

  if (defaultIndex.value === idx) {
    // Default row removed: stick to the next row if it exists, otherwise the last row.
    defaultIndex.value = Math.min(idx, entries.value.length - 1);
  } else if (idx < defaultIndex.value) {
    // A row before the default was removed, so the default shifts up by one.
    defaultIndex.value -= 1;
  }
};

const toTwoDecimalsOrEmpty = v => {
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) {
    return '';
  }
  return (Math.floor(n * 100) / 100).toFixed(2);
};

const sanitizeInput = raw => {
  const cleaned = String(raw ?? '').replace(/[^0-9.]/g, '');
  const [intPart, decPartRaw] = cleaned.split('.');
  if (decPartRaw !== undefined) {
    return `${intPart || '0'}.${decPartRaw.slice(0, 2)}`;
  }
  return intPart || '';
};

const onAmountInput = (idx, value) => {
  entries.value[idx] = sanitizeInput(value);
};

const onAmountKeydown = e => {
  const isDigit = /^[0-9]$/.test(e.key);
  const isDot = e.key === '.';
  const isControlKey =
    ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key) || e.ctrlKey || e.metaKey;
  if (isControlKey) {
    return;
  }

  const el = e.target;
  const value = String(el?.value ?? '');
  const selectionStart = el?.selectionStart ?? value.length;
  const selectionEnd = el?.selectionEnd ?? value.length;

  if (isDot && value.includes('.')) {
    e.preventDefault();
    return;
  }
  if (isDigit) {
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionEnd);
    const next = `${before}${e.key}${after}`;
    const match = next.match(/^\d*(?:\.(\d*))?/);
    const dec = match && match[1] ? match[1] : '';
    if (dec.length > 2) {
      e.preventDefault();
      return;
    }
  }
  if (!isDigit && !isDot) {
    e.preventDefault();
  }
};

const onAmountBlur = idx => {
  const v = entries.value[idx];
  entries.value[idx] = v === '' ? '' : toTwoDecimalsOrEmpty(v);
};

const majorToMinor = major => {
  const n = parseFloat(String(major).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) {
    return undefined;
  }
  return Math.round(n * 100);
};

const formatMinorToDisplay = minor => {
  const amount = minor / 100;
  return `${props.currencySymbol}${Number.isInteger(amount) ? String(amount) : amount.toFixed(2)}`;
};

const saveAndClose = () => {
  validationError.value = '';
  const minors = entries.value
    .map(v => majorToMinor(v))
    .filter(v => Number.isFinite(v) && v >= Math.round((props.minAmount || 0) * 100));

  if (minors.length === 0) {
    emit('input', undefined);
    emit('update:defaultMinor', undefined);
    closeModal();
    return;
  }

  const idx = Math.min(Math.max(0, defaultIndex.value), minors.length - 1);
  const defaultMinor = minors[idx];
  const sortedMinors = [...minors].sort((a, b) => a - b);

  // Emit the list of award values in ascending order (low to high)
  if (sortedMinors.length === 1) {
    emit('input', sortedMinors[0]);
  } else {
    emit('input', sortedMinors);
  }

  // Emit the designated default minor separately
  emit('update:defaultMinor', defaultMinor);

  // Keep the highlighted default in sync with the sorted list for the pill view
  const sortedIndex = sortedMinors.findIndex(v => v === defaultMinor);
  defaultIndex.value = sortedIndex >= 0 ? sortedIndex : 0;
  closeModal();
};
</script>

<style scoped lang="scss">
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.award-values-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.pill-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pill {
  border: 1px solid var(--palette-color-default-lighten-80);
  background: var(--palette-color-base-white);
  color: var(--palette-color-default);
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
}

.pill.active {
  color: var(--palette-color-brand-primary);
  background: var(--palette-color-core-1-lighten-80);
  border-color: var(--palette-color-brand-primary-lighten-30);
  font-weight: 500;
}

.pill.edit-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  transition: border-color 120ms ease, box-shadow 120ms ease, color 120ms ease, background-color 120ms ease;
}

.pill.edit-pill:hover {
  color: var(--palette-color-brand-primary);
  background: var(--palette-color-core-1-lighten-80);
  border-color: var(--palette-color-brand-primary-lighten-30);
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.intro .heading {
  font-weight: 600;
  font-size: 1rem;
}
.intro .sub {
  color: var(--palette-color-default-lighten-30);
}

.table-head {
  display: grid;
  grid-template-columns: 1fr 60px 60px;
  align-items: center;
  gap: 0.5rem;
  color: var(--palette-color-default);
  font-weight: 500;
}
.editor-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.editor-row {
  display: grid;
  grid-template-columns: 1fr 60px 60px;
  align-items: center;
  gap: 0.5rem;
}
.col-default,
.col-action {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.default-radio {
  margin: 0;
  padding-left: 0;
}
.default-radio :deep(.custom-control) {
  margin: 0;
  padding-left: 0;
}
.default-radio :deep(.custom-control-label) {
  padding-left: 0;
}
.default-radio :deep(.custom-control-label::before),
.default-radio :deep(.custom-control-label::after) {
  left: 50%;
  transform: translateX(-50%);
}
.amount-input {
  margin: 0;
  width: 100%;
}
.remove-btn {
  padding: 0.25rem 0.5rem;
}
.footer-actions {
  margin-top: 0.25rem;
}
.add-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: var(--palette-color-default) !important;
}
.add-button:hover,
.add-button:focus {
  color: var(--palette-color-default) !important;
  text-decoration: none;
}

:deep(.modal-dialog) {
  max-width: 550px;
}
</style>
